/**
 * Custom authentication hooks
 *
 * Provides unified hooks that work with both mock and OIDC authentication providers.
 * Abstracts the authentication provider implementation from components.
 *
 * Provider-agnostic: Works with Cognito, Entra, or any OIDC provider.
 */

import { useAuth as useOidcAuth } from 'react-oidc-context';
import type { User } from 'oidc-client-ts';

import { mapClaimsForProvider, type CanonicalUserClaims } from './claim-mapping';
import { getAuthProvider, buildLogoutArgs, buildOidcConfig } from './providers/oidc';
import { logAuthEvent, getStoredCorrelationId, generateCorrelationId } from './logging';
import { getConfig } from '../config';

/**
 * Authentication state and methods
 */
export interface AuthHook {
  /** Whether user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether authentication state is being determined */
  isLoading: boolean;
  /** Current authenticated user */
  user: User | null | undefined;
  /** Authentication error, if any */
  error: Error | undefined;
  /** Initiate sign in flow */
  signinRedirect: () => Promise<void>;
  /** Initiate sign out flow */
  signoutRedirect: () => Promise<void>;
  /** Remove user session */
  removeUser: () => Promise<void>;
  /** Silent token renewal */
  signinSilent: () => Promise<User | null>;
}

/**
 * Hook for accessing authentication state and methods
 *
 * Works with both MockAuthProvider and OidcAuthProvider.
 * Provides a consistent API regardless of the authentication provider.
 *
 * This hook directly re-exports useAuth from react-oidc-context,
 * which works for both mock and OIDC providers because they both
 * provide the same context interface.
 *
 * @returns Authentication state and methods
 */
export function useAuth(): AuthHook {
  // This works for both providers because:
  // - OidcAuthProvider wraps react-oidc-context's AuthProvider
  // - MockAuthProvider uses the same context structure
  const auth = useOidcAuth();

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user,
    error: auth.error,
    signinRedirect: auth.signinRedirect,
    signoutRedirect: auth.signoutRedirect,
    removeUser: auth.removeUser,
    signinSilent: auth.signinSilent,
  };
}

/**
 * User profile information (provider-agnostic)
 */
export interface UserProfile {
  /** User's email address */
  email: string;
  /** User's display name */
  name: string;
  /** User's unique identifier (provider-agnostic) */
  sub: string;
  /** User's groups/roles (normalized from provider-specific claims) */
  groups: string[];
  /** Authentication provider: 'cognito', 'entra', 'local' */
  provider: string;
  /** Preferred username (may be email) */
  preferredUsername?: string;
}

/**
 * Hook for accessing current user profile
 *
 * Extracts and normalizes user profile fields from the authentication user object.
 * Uses claim mapping to handle provider-specific claim names.
 * Returns null if user is not authenticated.
 *
 * @returns User profile or null
 */
export function useUser(): UserProfile | null {
  const auth = useAuth();

  if (!auth.isAuthenticated || !auth.user?.profile) {
    return null;
  }

  const profile = auth.user.profile;
  const provider = getAuthProvider();

  // Map claims using provider-specific mapping
  const canonicalClaims = mapClaimsForProvider(profile as Record<string, unknown>, provider);

  return {
    email: canonicalClaims.email || (profile.preferred_username as string) || '',
    name: canonicalClaims.name || canonicalClaims.email || 'User',
    sub: canonicalClaims.sub || profile.sub || '',
    groups: canonicalClaims.groups,
    provider: canonicalClaims.provider,
    preferredUsername: profile.preferred_username as string | undefined,
  };
}

/**
 * Hook for accessing canonical user claims
 *
 * Returns fully normalized claims that work across all providers.
 *
 * @returns Canonical user claims or null
 */
export function useCanonicalClaims(): CanonicalUserClaims | null {
  const auth = useAuth();

  if (!auth.isAuthenticated || !auth.user?.profile) {
    return null;
  }

  const profile = auth.user.profile;
  const provider = getAuthProvider();

  return mapClaimsForProvider(profile as Record<string, unknown>, provider);
}

/**
 * Hook for checking if user has a specific role or permission
 *
 * Uses normalized groups from claim mapping (provider-agnostic).
 * Returns false if user is not authenticated or groups are not available.
 *
 * @param role - Role to check for
 * @returns Whether user has the specified role
 */
export function useHasRole(role: string): boolean {
  const claims = useCanonicalClaims();

  if (!claims) {
    return false;
  }

  return claims.groups.includes(role);
}

/**
 * Hook for getting user's access token
 *
 * Useful for making authenticated API requests.
 * Returns null if user is not authenticated.
 *
 * @returns Access token or null
 */
export function useAccessToken(): string | null {
  const auth = useAuth();

  if (!auth.isAuthenticated || !auth.user) {
    return null;
  }

  // Use ID token (contains email, name, profile claims) instead of access token.
  // Cognito access tokens only contain sub/username (UUID), not email.
  // The backend OIDC validator accepts both; at_hash verification is disabled.
  return auth.user.id_token || auth.user.access_token || null;
}

/**
 * Provider-specific logout hook result
 */
export interface ProviderLogoutHook {
  /** Initiate provider-specific sign out flow */
  signoutRedirect: () => Promise<void>;
}

/**
 * Hook for provider-specific logout
 *
 * Handles provider-specific logout URL requirements:
 * - AWS Cognito: Requires manual redirect to /logout endpoint with client_id and logout_uri
 * - Entra ID: Uses standard OIDC id_token_hint
 * - Standard OIDC: Uses id_token_hint and post_logout_redirect_uri
 *
 * NOTE: AWS Cognito does NOT follow standard OIDC logout. The oidc-client-ts library
 * uses the discovery document's end_session_endpoint, which doesn't work correctly
 * with Cognito. For Cognito, we must manually construct the logout URL and redirect.
 *
 * @returns Provider logout hook with signoutRedirect function
 */
export function useProviderLogout(): ProviderLogoutHook {
  const auth = useOidcAuth();
  const provider = getAuthProvider();
  const oidcConfig = buildOidcConfig();

  const signoutRedirect = async (): Promise<void> => {
    const correlationId = getStoredCorrelationId() || generateCorrelationId();

    // Log logout initiation
    logAuthEvent('logout_initiated', 'User initiated sign-out flow', {
      correlationId,
      provider
    });

    // For local/mock auth, just remove the user
    if (!oidcConfig) {
      await auth.removeUser();
      logAuthEvent('logout_complete', 'User signed out (mock)', {
        correlationId,
        provider
      });
      return;
    }

    // Cognito sign-out: two-step process.
    //
    // Step 1: Set a flag and redirect to Cognito /logout to clear the Cognito
    //   session cookie. Cognito requires redirect_uri + response_type=code, so
    //   it will redirect through /authorize → login page. Chrome may auto-fill
    //   credentials, sending the user back to the app with a new ?code=.
    //
    // Step 2: onSigninCallback in AuthProvider.tsx detects the flag, rejects the
    //   auto-re-authentication by clearing all local state and redirecting to
    //   /?signed_out. AuthGate shows the sign-in page. The Cognito session is
    //   now gone, so clicking "Sign in" requires real credentials.
    if (provider === 'cognito') {
      // Get Cognito domain for the /logout URL
      let cognitoDomain: string | undefined;
      try {
        const envConfig = getConfig();
        cognitoDomain = envConfig.cognitoDomain;
      } catch {
        cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN ||
          import.meta.env.VITE_DEV_COGNITO_DOMAIN ||
          import.meta.env.VITE_PROD_COGNITO_DOMAIN;
      }

      if (!cognitoDomain && oidcConfig.authority) {
        try {
          const discoveryUrl = `${oidcConfig.authority}/.well-known/openid-configuration`;
          const response = await fetch(discoveryUrl);
          if (response.ok) {
            const metadata = await response.json();
            if (metadata.token_endpoint) {
              cognitoDomain = new URL(metadata.token_endpoint).hostname;
            }
          }
        } catch { /* ignore */ }
      }

      if (cognitoDomain) {
        cognitoDomain = cognitoDomain.replace(/^https?:\/\//, '');

        const logoutUrl = new URL(`https://${cognitoDomain}/logout`);
        logoutUrl.searchParams.set('client_id', oidcConfig.clientId);
        logoutUrl.searchParams.set('redirect_uri', oidcConfig.redirectUri);
        logoutUrl.searchParams.set('response_type', 'code');
        logoutUrl.searchParams.set('scope', oidcConfig.scope || 'openid email profile');

        logAuthEvent('logout_redirect', 'Redirecting to Cognito logout (step 1 of 2)', {
          correlationId,
          provider
        });

        // Set flag for onSigninCallback to intercept the re-auth (step 2)
        sessionStorage.setItem('cognito_logout_pending', 'true');

        window.location.href = logoutUrl.toString();
        return;
      }

      // Fallback: no Cognito domain — clear local state and show sign-in page
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(';').forEach((c) => {
        const name = c.split('=')[0].trim();
        if (name) {
          document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/`;
        }
      });
      window.location.replace(`${window.location.origin}?signed_out`);
      return;
    }

    // For other providers (Entra, standard OIDC), use the library's logout
    const logoutArgs = buildLogoutArgs(
      provider,
      oidcConfig.clientId,
      oidcConfig.postLogoutRedirectUri,
      auth.user?.id_token
    );

    await auth.signoutRedirect(logoutArgs);
  };

  return { signoutRedirect };
}
